/**
 * The literal token a configured GMR_LANDING_ZONE_ADMIN_URL must contain. The
 * browser swaps it for the current authority's account_id so that adding a new
 * environment is a single Hydra edit rather than a code change.
 */
export const ACCOUNT_ID_TOKEN = '{account_id}';

/**
 * Substitutes the authority's account id into the configured URL template.
 *
 * Returns '' when either input is missing so callers can gate on a falsy url
 * rather than building a half-formed src.
 *
 * A template with no {account_id} token is returned verbatim. Appending a
 * guessed query param instead would produce a URL that loads fine but is
 * unscoped or wrongly scoped — a silent data-correctness bug, and strictly
 * worse than an obviously misconfigured setting. The parameter name is GMR's
 * contract to define, not ours to infer.
 */
export const buildLandingZonesUrl = (
  template: string,
  accountId: string | undefined,
): string => {
  if (!template || !accountId) return '';
  // split/join rather than replaceAll (no es2021 lib dependency) or a regex
  // (no escaping question). encodeURIComponent is correct whether the token
  // sits in a path segment or a query value.
  return template.split(ACCOUNT_ID_TOKEN).join(encodeURIComponent(accountId));
};

/**
 * Sets the `theme` query param ('light' | 'dark') that the Landing Zone tool
 * reads to match UNITE's theme.
 *
 * Returns the url untouched when either input is empty or the url can't be
 * parsed — a missing theme is cosmetic, so it must never blank the iframe.
 */
export const withThemeParam = (url: string, theme: string): string => {
  if (!url || !theme) return url;
  try {
    const parsed = new URL(url);
    parsed.searchParams.set('theme', theme);
    return parsed.toString();
  } catch {
    return url;
  }
};

export const templateHasAccountIdToken = (template: string): boolean =>
  template.includes(ACCOUNT_ID_TOKEN);
